import { useMemo } from 'react';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { ShoppingCart, Receipt, Truck, Clock } from 'lucide-react';
import { SimpleTable } from '../k2/K2Tables';
import { fmtCz, fmtKc } from '../k2/k2utils';
import { Panel } from './PartnerRanking';

const bookBadge = (r) => (
  <span className="inline-flex items-center gap-1.5 whitespace-nowrap" title={r.book_name}>
    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[#F1F5F1] text-[#57534E]">{r.book}</span>
    {r.book_name && <span className="text-xs text-[#57534E]">{r.book_name}</span>}
  </span>
);
const date = { key: 'date', label: 'Datum', render: (r) => fmtCz(r.date), className: 'whitespace-nowrap' };
const doc = { key: 'doc', label: 'Doklad', className: 'font-medium whitespace-nowrap' };
const desc = { key: 'description', label: 'Popis', className: 'text-[#57534E]' };
const items = { key: 'item_count', label: 'Položek', num: true };
const amount = (label) => ({ key: 'amount', label, num: true, render: (r) => fmtKc(r.amount), className: 'font-semibold' });
const today = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Prague' });
const overdueDays = (r) => differenceInCalendarDays(new Date(), parseISO(r.due_date));

const BUCKETS = [
  { id: 'b30', label: '1–30 dní', test: (d) => d <= 30 },
  { id: 'b60', label: '31–60 dní', test: (d) => d > 30 && d <= 60 },
  { id: 'b90', label: '61–90 dní', test: (d) => d > 60 && d <= 90 },
  { id: 'b90p', label: 'Více než 90 dní', test: (d) => d > 90 },
];

const OverdueSection = ({ src, book }) => {
  const unpaid = src('unpaid');
  const rows = useMemo(() => unpaid.items.filter((r) => r.due_date && r.due_date < today() && (book === 'all' || r.book === book))
    .map((r) => ({ ...r, days: overdueDays(r) })).sort((a, b) => b.days - a.days), [unpaid.items, book]);
  return (
    <Panel title="Neuhrazené faktury po splatnosti" testId="an-overdue"
      subtitle={unpaid.loading ? 'Načítám…' : `${rows.length} faktur · celkem ${fmtKc(rows.reduce((a, r) => a + r.remaining, 0))} · aktuální stav bez ohledu na zvolené období`}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {BUCKETS.map((b) => {
          const r = rows.filter((x) => b.test(x.days));
          return (
            <div key={b.id} className="p-4 rounded-xl border border-[#E2E8F0] bg-[#F8FAF8]" data-testid={`an-overdue-${b.id}`}>
              <div className="text-xs font-semibold uppercase tracking-wide text-[#57534E]">{b.label}</div>
              <div className="mt-1 text-xl font-bold text-[#DC2626] font-['Manrope'] tabular-nums" data-testid={`an-overdue-${b.id}-value`}>{fmtKc(r.reduce((a, x) => a + x.remaining, 0))}</div>
              <div className="text-xs text-[#57534E]">{r.length} faktur</div>
            </div>
          );
        })}
      </div>
      <SimpleTable testId="an-overdue-table" rows={rows} rowKey={(r) => r.rid} columns={[
        doc, { key: 'book', label: 'Řada', render: bookBadge }, { key: 'partner', label: 'Dodavatel', className: 'font-medium' },
        { key: 'due_date', label: 'Splatnost', render: (r) => fmtCz(r.due_date), className: 'whitespace-nowrap' },
        { key: 'days', label: 'Dní po splatnosti', num: true, render: (r) => <span className="font-semibold text-[#DC2626]">{r.days}</span> },
        { key: 'remaining', label: 'Zbývá uhradit', num: true, render: (r) => fmtKc(r.remaining), className: 'font-semibold' },
        desc,
      ]} />
    </Panel>
  );
};

export const ANALYSIS_CONFIGS = {
  nakup: {
    id: 'nakup', title: 'Nákup – příjemky', icon: ShoppingCart,
    docsSource: { key: 'docs', url: '/api/k2/data/receipts' },
    itemsSource: { key: 'items', url: '/api/k2/data/receipt-items' },
    partnerLabel: 'Dodavatelé', partnerSingular: 'Dodavatel', amountLabel: 'Nákup celkem', showWarehouse: true, showPrices: true,
    docsTitle: 'Příjemky (řada 100)',
    docColumns: [date, doc, { key: 'partner', label: 'Dodavatel', className: 'font-medium' }, { key: 'warehouse', label: 'Sklad' }, desc, items, amount('Hodnota')],
  },
  'faktury-prijate': {
    id: 'faktury-prijate', title: 'Přijaté faktury', icon: Receipt,
    docsSource: { key: 'docs', url: '/api/k2/data/invoices-in' },
    extraSources: [{ key: 'unpaid', url: '/api/k2/invoices-in-unpaid', noRange: true }],
    partnerLabel: 'Dodavatelé', partnerSingular: 'Dodavatel', amountLabel: 'Fakturováno bez DPH', inverse: true,
    dynamicBooks: true,
    docsTitle: 'Přijaté faktury',
    extraTiles: ({ cur }) => [{
      id: 'remaining', label: 'Neuhrazeno (s DPH)', icon: Clock, color: '#DC2626', fmt: fmtKc,
      cur: cur.rows.reduce((a, r) => a + r.remaining, 0), note: `${cur.rows.filter((r) => !r.paid).length} faktur z období · celkem s DPH ${fmtKc(cur.rows.reduce((a, r) => a + r.amount_gross, 0))}`,
    }],
    ExtraSection: OverdueSection,
    docColumns: [date, doc, { key: 'book', label: 'Řada', render: bookBadge }, { key: 'partner', label: 'Dodavatel', className: 'font-medium' }, desc,
      { key: 'due_date', label: 'Splatnost', render: (r) => fmtCz(r.due_date), className: 'whitespace-nowrap' },
      { key: 'paid', label: 'Stav', render: (r) => (r.paid
        ? <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">Uhrazeno</span>
        : <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${r.due_date < today() ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>{r.due_date < today() ? 'Po splatnosti' : 'Neuhrazeno'}</span>) },
      amount('Bez DPH'), { key: 'amount_gross', label: 'S DPH', num: true, render: (r) => fmtKc(r.amount_gross), className: 'text-[#57534E]' }],
  },
  spotreba: {
    id: 'spotreba', title: 'Výdeje Expediční sklady', icon: Truck,
    docsSource: { key: 'docs', url: '/api/k2/data/consumption' },
    itemsSource: { key: 'items', url: '/api/k2/data/consumption-items' },
    partnerLabel: 'Odběratelé', partnerSingular: 'Odběratel', amountLabel: 'Vydáno celkem', showWarehouse: true, showPrices: false,
    docsTitle: 'Výdejky (řada 230)',
    docColumns: [date, doc, { key: 'partner', label: 'Odběratel', className: 'font-medium' }, { key: 'warehouse', label: 'Sklad' }, desc, items, amount('Hodnota')],
  },
};
